import React from 'react'
import PropTypes from 'prop-types'
import { styled } from 'styled-components'
import * as Sentry from '@sentry/react'
import i18n from '../../../i18n'

import theme from '../../theme'
import { ButtonPrimary } from '../generic/buttons'
import { IconSync } from '../icons'

const StyledErrorBoundary = styled.div`
  background-color: ${theme.color.background};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  z-index: 102;
  border: 1px;
`

const ErrorBoundaryContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const ErrorBoundaryPrimaryText = styled.span`
  font-weight: 700;
`

const ErrorBoundaryStatusContainer = styled.span`
  margin: 0 1rem 0 0;
`

const ErrorBoundaryStatus = styled.div`
  height: 9rem;
  width: 1rem;
  background-color: ${theme.color.cautionColor};
`

const ErrorButtonContainer = styled.span`
  min-width: 160px;
`

const ErrorButton = styled(ButtonPrimary)`
  margin-left: 5rem;
`

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      errorMessage: '',
      attemptedRerender: false,
    }
  }

  static getDerivedStateFromError(error) {
    return { errorMessage: error.toString() }
  }

  componentDidCatch(error, info) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } })
  }

  render() {
    const { errorMessage, attemptedRerender } = this.state
    const { children } = this.props

    if (errorMessage) {
      // Render a fallback UI
      // There is potential to pass this in as a property to make the error boundary customizable
      return (
        <StyledErrorBoundary>
          <ErrorBoundaryStatusContainer>
            <ErrorBoundaryStatus />
          </ErrorBoundaryStatusContainer>
          <ErrorBoundaryContentContainer>
            <ErrorBoundaryPrimaryText data-testid="error-boundary-message">
              {i18n.t('page.not_loaded')}
            </ErrorBoundaryPrimaryText>
            <p>
              {i18n.t('page.try_reloading')}{' '}
              <a target="_blank" href="https://datamermaid.org/contact-us" rel="noreferrer">
                {i18n.t('contact_us')}
              </a>
              .
            </p>
          </ErrorBoundaryContentContainer>
          <ErrorButtonContainer>
            {!attemptedRerender && (
              <ErrorButton
                data-testid="error-boundary-try-again"
                onClick={() => {
                  this.setState({
                    errorMessage: '',
                    attemptedRerender: true,
                  })
                }}
              >
                <IconSync />
                <span> {i18n.t('try_again')}</span>
              </ErrorButton>
            )}
          </ErrorButtonContainer>
        </StyledErrorBoundary>
      )
    }

    return children
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
}

export default ErrorBoundary
